import React, { PropsWithChildren, useEffect, useRef, useState } from 'react';
import {
  Dimensions,
  Keyboard,
  KeyboardAvoidingView,
  KeyboardEvent,
  LayoutChangeEvent,
  StyleSheet,
  View,
  ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { isAndroid, isIOS } from 'shared/constants/platform';
import theme from 'shared/styles/theme';

/**
 * Content slot component for the KeyboardAvoidingContainer
 * This is a marker component that simply returns null
 */
function Content(_props: PropsWithChildren): null {
  return null;
}

/**
 * Actions slot component for the KeyboardAvoidingContainer
 * This is a identity component that simply returns null
 */
function Actions(_props: PropsWithChildren): null {
  return null;
}

interface KeyboardAvoidingContainerProps {
  /**
   * Container style overrides
   */
  containerStyle?: ViewStyle | ViewStyle[];

  /**
   * Content container style overrides
   */
  contentContainerStyle?: ViewStyle | ViewStyle[];

  /**
   * Actions container style overrides
   */
  actionsContainerStyle?: ViewStyle | ViewStyle[];

  /**
   * iOS keyboard vertical offset
   * @default 100
   */
  keyboardVerticalOffset?: number;

  /**
   * Children using the slot pattern
   */
  children?: React.ReactNode;
}

// Type guard function to check if an element is a Content component
function isContentElement(
  element: React.ReactElement,
): element is React.ReactElement<PropsWithChildren> {
  return element.type === Content;
}

// Type guard function to check if an element is an Actions component
function isActionsElement(
  element: React.ReactElement,
): element is React.ReactElement<PropsWithChildren> {
  return element.type === Actions;
}

/**
 * A reusable keyboard avoiding container that handles both Android and iOS
 * keyboard behavior for any content and actions.
 */
function KeyboardAvoidingContainer(props: KeyboardAvoidingContainerProps) {
  const {
    containerStyle,
    contentContainerStyle,
    actionsContainerStyle,
    keyboardVerticalOffset = 100,
    children,
  } = props;

  const insets = useSafeAreaInsets();
  const [keyboardHeight, setKeyboardHeight] = useState(0);
  const screenHeight = Dimensions.get('window').height;
  const containerRef = useRef<View>(null);
  const [_containerHeight, setContainerHeight] = useState(0);

  // Extract content and actions from children
  let contentElements: React.ReactNode = null;
  let actionsElements: React.ReactNode = null;

  // Find Content and Actions components among children
  React.Children.forEach(children, (child) => {
    if (!React.isValidElement(child)) {
      return;
    }

    // Use type guards to safely extract children
    if (isContentElement(child)) {
      contentElements = child.props.children;
    } else if (isActionsElement(child)) {
      actionsElements = child.props.children;
    }
  });

  // Get the container height
  const onLayout = (event: LayoutChangeEvent) => {
    const { height } = event.nativeEvent.layout;
    setContainerHeight(height);
  };

  useEffect(() => {
    function onKeyboardDidShow(e: KeyboardEvent) {
      if (isAndroid) {
        const keyboardHeight = e.endCoordinates.height;

        setKeyboardHeight(keyboardHeight);
      }
    }

    function onKeyboardDidHide() {
      if (isAndroid) {
        setKeyboardHeight(0);
      }
    }

    const showSubscription = Keyboard.addListener(
      'keyboardDidShow',
      onKeyboardDidShow,
    );
    const hideSubscription = Keyboard.addListener(
      'keyboardDidHide',
      onKeyboardDidHide,
    );
    return () => {
      showSubscription.remove();
      hideSubscription.remove();
    };
  }, [screenHeight]);

  return (
    <View
      ref={containerRef}
      onLayout={onLayout}
      style={[
        styles.container,
        isAndroid ? { marginBottom: keyboardHeight } : {},
        containerStyle,
      ]}
    >
      {children}
      <View style={[styles.contentContainer, contentContainerStyle]}>
        {contentElements}
      </View>
      <KeyboardAvoidingView
        enabled={isIOS}
        behavior="padding"
        keyboardVerticalOffset={keyboardVerticalOffset}
      >
        <View
          style={[
            styles.actionsContainer,
            { paddingBottom: insets.bottom ? insets.bottom : theme.spacing.m },
            actionsContainerStyle,
          ]}
        >
          {actionsElements}
        </View>
      </KeyboardAvoidingView>
    </View>
  );
}

const styles = StyleSheet.create({
  actionsContainer: {
    gap: theme.spacing.m,
    justifyContent: 'flex-end',
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'space-between',
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  contentContainer: {
    flex: 1,
    gap: theme.spacing.m,
    width: '100%',
  },
});

// Add Content and Actions as static properties
KeyboardAvoidingContainer.Content = Content;
KeyboardAvoidingContainer.Actions = Actions;

export default KeyboardAvoidingContainer;
